/**
 * useChatDealHydration — keep `dealContext.active_term_sheet_id` fresh.
 *
 * Citations Phase 1. Watches `dealContext.deal_id` and fetches the deal's
 * term sheets via the existing TanStack hook. Picks the latest ANALYZED
 * sheet whose source PDF is still available and writes its id back to the
 * store. Drives the "View term sheet" link below assistant messages.
 *
 * Why a separate hook instead of populating at every `setDealContext`
 * call site: the 6 callers (ListDealsCard, useChatHistoryActions,
 * ChatUploadCard, ChatDealCreationCard, ChatDealSelectionCard) do not
 * have a term sheet id at hand. Resolving it lazily here avoids a cached
 * value going stale (a newly uploaded sheet completing extraction
 * mid-session) and keeps signed URLs out of the 24h-persisted store —
 * we mint them on demand at click time from
 * GET /term-sheets/{id}/download-url.
 *
 * Cycle safety: only writes back when the resolved id actually differs
 * from the stored one. The persist middleware bumps `lastTouchedAt` on
 * every `setDealContext`, so an unconditional write would re-fire the
 * effect indefinitely.
 *
 * Mount once per chat surface (ChatPage + ChatPanel).
 */
import { useEffect } from 'react';

import { useTermSheets } from '../../../api/hooks/useTermSheets';
import { useChatStore } from '../../../stores/chatStore';

export function useChatDealHydration() {
  const dealContext = useChatStore((s) => s.dealContext);
  const setDealContext = useChatStore((s) => s.setDealContext);

  const dealId = dealContext?.deal_id ?? 0;
  const { data } = useTermSheets(dealId);

  useEffect(() => {
    if (!dealContext?.deal_id) {
      return;
    }

    const sheets = data?.term_sheets ?? [];
    const latestAnalyzed = sheets
      .filter(
        (ts) => ts.status === 'ANALYZED' && ts.pdf_available !== false,
      )
      .sort((a, b) => b.id - a.id)[0];

    const nextId = latestAnalyzed?.id;
    if (nextId === dealContext.active_term_sheet_id) {
      return;
    }

    setDealContext({
      ...dealContext,
      active_term_sheet_id: nextId,
    });
  }, [dealContext, data, setDealContext]);
}
